/*!
 * john-widget-cards.js — lifecycle-aware product-card renderer for the John Sheridan widget.
 *
 * Dependency-free, drop-in for the theme widget (same house style as john-widget.js). Renders
 * the two additive payloads the phase-2 backend now returns under an answer bubble:
 *
 *   1) data.products        -> a small tile ROW (presales + comparison answers).
 *   2) data.availability    -> the single-model AVAILABILITY TREE (in-stock / lead-time /
 *                              clearance-comparison / replacement / contact-trio).
 *
 * Both are OPTIONAL and unknown-field-safe: old bubbles that don't call these just ignore them.
 * Cards are already filtered server-side (ACTIVE+published, headline-only, no aerospace, exact
 * model match). Lifecycle fields decide the badge + CTA here; the widget never invents state.
 *
 * Card fields consumed (from shopify_cards._to_card):
 *   model, title, url, image, image_alt, price, currency, compare_at_price, on_sale,
 *   available, discontinued, clearance, clearance_pct, inventory, lifecycle,
 *   availability_text, replacement (nested card | null), packing {docs, included_accessories,
 *   has_contents, datasheet_fallback_url}
 *
 * Availability payload (from shopify_cards.availability_for):
 *   { model, lifecycle, in_stock, message, cards[], contact|null, clearance_pct? }
 *
 * Usage in the widget, after a /api/chat response:
 *   if (data.availability) JohnCards.renderAvailability(bubbleEl, data.availability);
 *   else if (data.products && data.products.length) JohnCards.renderProducts(bubbleEl, data.products);
 *
 * CTA is VIEW-ONLY for now (SPEC §6 open call); "Add to cart" is a later Cart-AJAX decision.
 */
(function (global) {
  "use strict";

  var CONTACT_FALLBACK = {
    url: "https://uni-trendus.com/pages/contact-us",
    phone: "888-668-8648",
    email: "sales@uni-trendus.com",
  };

  // ---- helpers -------------------------------------------------------------------------

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function money(amount, currency) {
    if (amount == null || amount === "") return "";
    var n = Number(amount);
    if (isNaN(n)) return esc(amount);
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "USD",
      }).format(n);
    } catch (e) {
      return "$" + n.toFixed(2);
    }
  }

  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  // ---- single card ---------------------------------------------------------------------

  function badge(card) {
    if (card.discontinued && card.available) {
      var left = typeof card.inventory === "number" ? " · " + card.inventory + " left" : "";
      // 🔴 2026-09-03e — THE RED "CLEARANCE" PILL NEEDED NO EVIDENCE. This branch fired on
      // anything discontinued-and-in-stock: no clearanceNN tag, no discount, no price check.
      // UDP3303A (36 units) and UT8802E are FULL PRICE and were carrying a clearance badge on
      // the page we are about to widen. Raised as owed by -09-02e; the answer TEXT was always
      // correct (generate.py says "selling through the last of the stock" and gates its "at N%
      // off" on a real pct), so the false claim lived only here, in the loudest element.
      //
      // 🔑 GATED ON `!= null`, NEVER ON TRUTHINESS. _clearance_pct returns 0 for a bare
      // `clearance` tag, so `if (card.clearance_pct)` would read a genuinely-tagged model as
      // not-on-clearance — dropping the honest cases while fixing the dishonest ones.
      //
      // 🔑 AND GATED ON clearance_pct, NOT ON card.clearance. That field is being corrected
      // server-side in the same push, but keying on it here would make this file's
      // correctness depend on DEPLOY ORDER. clearance_pct has always required the tag, so
      // this is right against the old server and the new one alike.
      if (card.clearance_pct != null) {
        var pct = card.clearance_pct ? " " + card.clearance_pct + "% off" : "";
        return '<span class="jc-badge jc-badge--clearance">Clearance' + pct + left + "</span>";
      }
      // Discontinued, in stock, no evidence of a discount. Neutral grey, and the unit count
      // stays — it is a fact. The word that had to go is "Clearance". (Pete, 2026-09-03e.)
      return '<span class="jc-badge jc-badge--eol">Discontinued' + (left || " · limited stock") + "</span>";
    }
    if (card.discontinued) {
      return '<span class="jc-badge jc-badge--eol">Discontinued</span>';
    }
    if (card.available) {
      return '<span class="jc-badge jc-badge--stock">In stock</span>';
    }
    return '<span class="jc-badge jc-badge--lead">6–8 wk lead time</span>';
  }

  function priceBlock(card) {
    var price = money(card.price, card.currency);
    if (!price) return "";
    if (card.on_sale && card.compare_at_price) {
      return (
        '<span class="jc-price">' +
        '<s class="jc-price-was">' + esc(money(card.compare_at_price, card.currency)) + "</s> " +
        '<span class="jc-price-now">' + esc(price) + "</span></span>"
      );
    }
    return '<span class="jc-price">' + esc(price) + "</span>";
  }

  // recommended = highlight a successor/replacement card
  function renderCard(card, opts) {
    opts = opts || {};
    var wrap = el("a", "jc-card" + (opts.recommended ? " jc-card--recommended" : ""));
    wrap.href = card.url || "#";
    wrap.target = "_blank";
    wrap.rel = "noopener";

    var img = card.image
      ? '<img class="jc-card-img" src="' + esc(card.image) + '" alt="' + esc(card.image_alt || card.title) + '" loading="lazy">'
      : '<div class="jc-card-img jc-card-img--empty"></div>';

    var tag = opts.recommended ? '<div class="jc-reco">Recommended</div>' : "";

    wrap.innerHTML =
      tag +
      img +
      '<div class="jc-card-body">' +
      '<div class="jc-card-title">' + esc(card.title || card.model) + "</div>" +
      '<div class="jc-card-meta">' + badge(card) + priceBlock(card) + "</div>" +
      '<span class="jc-cta">View product →</span>' +
      "</div>";
    return wrap;
  }

  // ---- public: product row (presales / comparison) -------------------------------------

  function renderProducts(container, products) {
    if (!container || !products || !products.length) return;
    injectStyles();
    var row = el("div", "jc-row");
    products.forEach(function (c) {
      row.appendChild(renderCard(c));
    });
    container.appendChild(row);
  }

  // ---- public: availability tree (single asked-for model) ------------------------------

  function renderAvailability(container, a) {
    if (!container || !a) return;
    injectStyles();
    var box = el("div", "jc-avail jc-avail--" + esc(a.lifecycle || "active"));

    // 🔴 2026-09-02d — `a.message` is NOT rendered. The answer bubble this box is
    // appended to ALREADY carries the sentence: generate.ask()'s availability fast path
    // composes the customer-facing text from this same payload and returns it as `answer`.
    // Printing `message` here put the Fort Worth closer and the 6-8 week lead-time line on
    // screen TWICE, ~40px apart. Measured on the live widget 2026-09-02.
    //
    // 🔑 THIS IS THE THIRD SURFACE TO SHIP THIS EXACT BUG. app.py printed it and was
    // fixed in `-p`; shopify_cards.availability_for still documents `message` as
    // "currently UNRENDERED ... app.py stopped printing this line in `-p` to kill a
    // duplicate" — a contract this file was written without reading. The field stays in
    // the payload as a fallback for any surface that has no composed answer; a surface
    // that HAS one must not print it.
    //   if (a.message) box.appendChild(el("p", "jc-avail-msg", esc(a.message)));

    if (a.cards && a.cards.length) {
      var row = el("div", "jc-row");
      a.cards.forEach(function (c, i) {
        // in the clearance-comparison, card[0] = asked-for, card[1] = recommended successor
        var recommended =
          a.lifecycle === "discontinued_clearance" ? i === 1 : a.lifecycle === "discontinued_replaced";
        row.appendChild(renderCard(c, { recommended: recommended }));
      });
      box.appendChild(row);
    }

    var contact = a.contact || (a.lifecycle === "discontinued_eol" ? CONTACT_FALLBACK : null);
    if (contact) box.appendChild(renderContact(contact));

    container.appendChild(box);
  }

  function renderContact(c) {
    return el(
      "div",
      "jc-contact",
      "Reach our team: " +
        '<a href="' + esc(c.url) + '" target="_blank" rel="noopener">Contact us</a> · ' +
        '<a href="tel:' + esc((c.phone || "").replace(/[^0-9+]/g, "")) + '">' + esc(c.phone) + "</a> · " +
        '<a href="mailto:' + esc(c.email) + '">' + esc(c.email) + "</a>"
    );
  }

  // ---- public: "what's in the box" -----------------------------------------------------

  function renderPacking(container, card) {
    if (!container || !card || !card.packing) return;
    injectStyles();
    var p = card.packing;
    var parts = [];
    if (p.docs) parts.push("<li>" + esc(p.docs) + "</li>");
    if (p.included_accessories) parts.push("<li>" + esc(p.included_accessories) + "</li>");
    if (parts.length) {
      container.appendChild(
        el("div", "jc-packing", "<strong>What's in the box</strong><ul>" + parts.join("") + "</ul>")
      );
    } else if (p.datasheet_fallback_url) {
      container.appendChild(
        el(
          "div",
          "jc-packing",
          'The full packing list is on the <a href="' +
            esc(p.datasheet_fallback_url) +
            '" target="_blank" rel="noopener">datasheet</a>.'
        )
      );
    }
  }

  // ---- styles (injected once) ----------------------------------------------------------

  var _styled = false;
  function injectStyles() {
    if (_styled) return;
    _styled = true;
    var css =
      ".jc-row{display:flex;flex-wrap:wrap;gap:12px;margin:10px 0}" +
      ".jc-card{flex:1 1 160px;max-width:220px;border:1px solid #e3e6ea;border-radius:10px;" +
      "overflow:hidden;text-decoration:none;color:inherit;background:#fff;display:flex;" +
      "flex-direction:column;transition:box-shadow .15s;position:relative}" +
      ".jc-card:hover{box-shadow:0 2px 10px rgba(0,0,0,.09)}" +
      ".jc-card--recommended{border-color:#1c7c54;box-shadow:0 0 0 1px #1c7c54 inset}" +
      ".jc-reco{background:#1c7c54;color:#fff;font-size:11px;font-weight:600;padding:2px 8px}" +
      ".jc-card-img{width:100%;height:120px;object-fit:contain;background:#f7f8f9}" +
      ".jc-card-img--empty{display:block}" +
      ".jc-card-body{padding:8px 10px;display:flex;flex-direction:column;gap:6px}" +
      ".jc-card-title{font-size:13px;font-weight:600;line-height:1.3}" +
      ".jc-card-meta{display:flex;flex-wrap:wrap;align-items:center;gap:6px}" +
      ".jc-price{font-size:13px}.jc-price-was{color:#8a9099}.jc-price-now{color:#c0341d;font-weight:600}" +
      ".jc-badge{font-size:11px;font-weight:600;padding:2px 7px;border-radius:999px;white-space:nowrap}" +
      ".jc-badge--stock{background:#e6f4ea;color:#1c7c54}" +
      ".jc-badge--clearance{background:#fdecea;color:#c0341d}" +
      ".jc-badge--eol{background:#eceef0;color:#5a636c}" +
      ".jc-badge--lead{background:#fff4e0;color:#9a6700}" +
      ".jc-cta{font-size:12px;color:#1a73c7;font-weight:600}" +
      ".jc-avail-msg{margin:6px 0;font-size:14px}" +
      ".jc-contact{font-size:13px;margin-top:6px}.jc-contact a{color:#1a73c7;text-decoration:none}" +
      ".jc-packing{font-size:13px;margin-top:8px}.jc-packing ul{margin:4px 0 0;padding-left:18px}";
    var s = document.createElement("style");
    s.setAttribute("data-john-cards", "1");
    s.appendChild(document.createTextNode(css));
    document.head.appendChild(s);
  }

  global.JohnCards = {
    renderProducts: renderProducts,
    renderAvailability: renderAvailability,
    renderPacking: renderPacking,
    _money: money, // exported for the widget's own use
  };
})(typeof window !== "undefined" ? window : this);